import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import styled from "styled-components";
import { FaGoogle, FaFacebook, FaEnvelope, FaLock } from "react-icons/fa";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { login, loginWithGoogle, loginWithFacebook } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError("");
      await login(email, password);
    } catch (err) {
      setError("Failed to sign in. Please check your credentials.");
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError("");
      await loginWithGoogle();
    } catch (err) {
      setError("Failed to sign in with Google.");
    }
  };

  const handleFacebookLogin = async () => {
    try {
      setError("");
      await loginWithFacebook();
    } catch (err) {
      setError("Failed to sign in with Facebook.");
    }
  };

  return (
    <LoginContainer>
      <LoginBox>
        <Title>Welcome Back</Title>
        <Subtitle>Sign in to continue shopping</Subtitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <InputIcon>
              <FaEnvelope />
            </InputIcon>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <FaLock />
            </InputIcon>
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </InputGroup>

          <LoginButton type="submit">
            Sign In
          </LoginButton>
        </Form>

        <Divider>
          <span>or continue with</span>
        </Divider>
        
        <SocialButton onClick={handleGoogleLogin} google>
          <FaGoogle /> Sign in with Google
        </SocialButton>
        
        <SocialButton onClick={handleFacebookLogin} facebook>
          <FaFacebook /> Sign in with Facebook
        </SocialButton>

        <SignupPrompt>
          Don't have an account? <Link to="/signup">Sign Up</Link>
        </SignupPrompt>
      </LoginBox>
    </LoginContainer>
  );
};

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background};
`;

const LoginBox = styled.div`
  background: white;
  padding: ${({ theme }) => theme.spacing.large};
  border-radius: 15px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  font-size: 2rem;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.large};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const InputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 12px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  padding-left: 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 12px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.accent};
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: ${({ theme }) => theme.spacing.large} 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  span {
    padding: 0 10px;
    color: ${({ theme }) => theme.colors.textLight};
    font-size: 0.9rem;
  }
`;

const SocialButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: 12px;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  
  ${({ google }) => google && `
    background-color: #4285f4;
    color: white;
    &:hover {
      background-color: #357ae8;
    }
  `}
  
  ${({ facebook }) => facebook && `
    background-color: #3b5998;
    color: white;
    &:hover {
      background-color: #2d4373;
    }
  `}
`;

const ErrorMessage = styled.div`
  background-color: #ff5f5f20;
  color: #ff5f5f;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  text-align: center;
`;

const SignupPrompt = styled.p`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.medium};
  color: ${({ theme }) => theme.colors.textLight};

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default Login;
